// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageProps from "@insite/client-framework/Types/PageProps";
import MobiusPage from "@insite/mobius/Page";
import PageModule from "@store/Framework/Types/PageModule";
import * as React from "react";

export type CanadianTimeZones = "PT" | "MT" | "CT" | "ET" | "AT" | "Local";

const enum fields {
    signedUsersOnly = "signedUsersOnly",
    isFeatured = "isFeatured",
    startDate = "startDate",
    endDate = "endDate",
    hideHourMinutes = "hideHourMinutes",
    dateTimezone = "dateTimezone",
    isLocalBranch = "isLocalBranch",
    localBranchTitle = "localBranchTitle",
    addressWrapper = "addressWrapper",
    provinceSelector = "provinceSelector",
    categoriesList = "categoriesList",
    previewImage = "previewImage",
    eventSummary = "eventSummary",
    linkLabel = "linkLabel",
    addInfoTitle = "addInfoTitle",
    addInfoPhone = "addInfoPhone",
    addInfoEmail = "addInfoEmail",
}

export interface AddressModel {
    fields: {
        branchName?: string;
        addressLabel: string;
    };
}

interface OwnProps extends PageProps {
    fields: {
        [fields.signedUsersOnly]: boolean;
        [fields.isFeatured]: boolean;
        [fields.startDate]: Date | null;
        [fields.endDate]: Date | null;
        [fields.hideHourMinutes]: boolean;
        [fields.dateTimezone]: CanadianTimeZones;
        [fields.isLocalBranch]: boolean;
        [fields.localBranchTitle]: string;
        [fields.addressWrapper]: AddressModel[];
        [fields.provinceSelector]: string[];
        [fields.categoriesList]: string[];
        [fields.previewImage]: string;
        [fields.eventSummary]: string;
        [fields.linkLabel]: string;
        [fields.addInfoTitle]: string;
        [fields.addInfoPhone]: string;
        [fields.addInfoEmail]: string;
    };
}

const EventDetailsPage: React.FunctionComponent<OwnProps> = ({ id }) => (
    <MobiusPage>
        <Zone contentId={id} zoneName="Content" />
    </MobiusPage>
);

const getTimezone = (tz: string) => {
    const currentDate = new Date();
    return currentDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        timeZone: tz,
    });
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const pageModule: PageModule<Toolkit.FieldDefinition> = {
    component: EventDetailsPage,
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        allowedParents: ["EventListPage"],
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            {
                ...HorizontalRule,
                sortOrder: 150,
            },
            {
                name: fields.signedUsersOnly,
                displayName: "Signed in Users Only can view",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.isFeatured,
                displayName: "Event is Featured and on top of the page",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.startDate,
                displayName: "Start Date",
                defaultValue: null,
                editorTemplate: "UTCDateTimeField",
                fieldType: "General",
                isRequired: true,
                tab: settingsTab,
            },
            {
                name: fields.endDate,
                displayName: "End Date",
                defaultValue: null,
                editorTemplate: "UTCDateTimeField",
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.hideHourMinutes,
                displayName: "Hide Hour and Minutes from Date",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.dateTimezone,
                displayName: "Timezone",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: `Pacific Time (${getTimezone("America/Vancouver")})`, value: "PT" },
                    { displayName: `Mountain Time (${getTimezone("America/Edmonton")})`, value: "MT" },
                    { displayName: `Central Time (${getTimezone("America/Winnipeg")})`, value: "CT" },
                    { displayName: `Eastern Time (${getTimezone("America/Toronto")})`, value: "ET" },
                    { displayName: `Atlantic Time (${getTimezone("America/Halifax")})`, value: "AT" },
                    { displayName: `Local Time`, value: "Local" },
                ],
                defaultValue: "Local",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.hideHourMinutes === false,
                tab: settingsTab,
            },
            {
                name: "horizontalRule1",
                editorTemplate: "HorizontalRule",
                defaultValue: "",
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: "addressLabel",
                displayName: "Address",
                editorTemplate: "LabelField",
                defaultValue: "",
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.isLocalBranch,
                displayName: "Use store locator link only",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                variant: "toggle",
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.localBranchTitle,
                displayName: "Local Branch Title",
                defaultValue: "At your local Branch",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                tooltip: "Links to Store Locator page",
                isVisible: widget => widget.fields.isLocalBranch === true,
                tab: settingsTab,
            },
            {
                name: fields.addressWrapper,
                editorTemplate: "ListField",
                getDisplay: ({ fields: { addressLabel } }) => addressLabel || "Address",
                defaultValue: [],
                fieldType: "Translatable",
                tab: settingsTab,
                isVisible: widget => widget.fields.isLocalBranch === false,
                fieldDefinitions: [
                    {
                        name: "branchName",
                        defaultValue: "",
                        editorTemplate: "TextField",
                    },
                    {
                        name: "addressLabel",
                        defaultValue: "Address",
                        editorTemplate: "TextField",
                        isRequired: true,
                    },
                ],
            },
            {
                name: fields.provinceSelector,
                displayName: "Province",
                editorTemplate: "SystemListCheckboxField",
                systemListName: "RSLProvinces",
                defaultValue: [],
                fieldType: "General",
                tooltip: "To add further items to this list, go to Admin, then System lists.",
                tab: settingsTab,
            },
            {
                name: "horizontalRule2",
                editorTemplate: "HorizontalRule",
                defaultValue: "",
                fieldType: "General",
                tab: settingsTab,
            },

            {
                name: fields.previewImage,
                displayName: "Preview Image",
                fieldType: "Translatable",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tooltip: "Image format in Event Detail Page is 16:9",
                tab: settingsTab,
            },
            {
                name: fields.categoriesList,
                displayName: "Categories",
                editorTemplate: "SystemListCheckboxField",
                systemListName: "RSLCategories",
                defaultValue: [],
                fieldType: "General",
                tooltip: "To add further items to this list, go to Admin, then System lists.",
                tab: settingsTab,
            },
            {
                name: fields.eventSummary,
                displayName: "Summary",
                defaultValue: "",
                editorTemplate: "MultilineTextField",
                fieldType: "Translatable",
                isRequired: true,
                tab: settingsTab,
            },
            {
                name: fields.linkLabel,
                displayName: "Button Label",
                defaultValue: "Learn More",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
                tab: settingsTab,
            },
            {
                name: "horizontalRule3",
                editorTemplate: "HorizontalRule",
                defaultValue: "",
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.addInfoTitle,
                displayName: "Additional Information Title",
                defaultValue: "Have Questions",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                tab: settingsTab,
            },
            {
                name: fields.addInfoPhone,
                displayName: "Phone number",
                defaultValue: "1-800-663-9635",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                tab: settingsTab,
            },
            {
                name: fields.addInfoEmail,
                displayName: "Email Address",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                tab: settingsTab,
            },
        ],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "EventDetailsPage" instead of this constant.
 */
export const EventDetailsPageContext = "EventDetailsPage";
